import { toLocationSlug } from './to-location-slug';

export type LocationLocale = 'en' | 'fi' | 'sv';

/**
 * Maps single-segment SRP location slugs (country- or continent-wide filters).
 * URL segment must match the key exactly (Algolia / router use these forms).
 */
const SINGLE_SEGMENT_REGION_SLUGS: Record<string, Record<LocationLocale, string>> = {
  Finland: { en: 'Finland', fi: 'Suomi', sv: 'Finland' },
  Suomi: { en: 'Finland', fi: 'Suomi', sv: 'Finland' },
  Europe: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
  Eurooppa: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
  Europa: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
};

const CITY_MAPPINGS: Record<string, Record<LocationLocale, string>> = {
  Helsinki: { en: 'Helsinki', fi: 'Helsinki', sv: 'Helsingfors' },
  Mikkeli: { en: 'Mikkeli', fi: 'Mikkeli', sv: 'S:t Michel' },
  Tampere: { en: 'Tampere', fi: 'Tampere', sv: 'Tammerfors' },
  Turku: { en: 'Turku', fi: 'Turku', sv: 'Åbo' },
  Oulu: { en: 'Oulu', fi: 'Oulu', sv: 'Uleåborg' },
  Espoo: { en: 'Espoo', fi: 'Espoo', sv: 'Esbo' },
  Vantaa: { en: 'Vantaa', fi: 'Vantaa', sv: 'Vanda' },
  Lahti: { en: 'Lahti', fi: 'Lahti', sv: 'Lahtis' },
  Kuopio: { en: 'Kuopio', fi: 'Kuopio', sv: 'Kuopio' },
  'Jyväskylä': { en: 'Jyväskylä', fi: 'Jyväskylä', sv: 'Jyväskylä' },
  Pori: { en: 'Pori', fi: 'Pori', sv: 'Björneborg' },
  Lappeenranta: { en: 'Lappeenranta', fi: 'Lappeenranta', sv: 'Villmanstrand' },
  Vaasa: { en: 'Vaasa', fi: 'Vaasa', sv: 'Vasa' },
  Kotka: { en: 'Kotka', fi: 'Kotka', sv: 'Kotka' },
  Joensuu: { en: 'Joensuu', fi: 'Joensuu', sv: 'Joensuu' },
  Hämeenlinna: { en: 'Hämeenlinna', fi: 'Hämeenlinna', sv: 'Tavastehus' },
  Seinäjoki: { en: 'Seinäjoki', fi: 'Seinäjoki', sv: 'Seinäjoki' },
  Rovaniemi: { en: 'Rovaniemi', fi: 'Rovaniemi', sv: 'Rovaniemi' },
};

const COUNTRY_MAPPINGS: Record<string, Record<LocationLocale, string>> = {
  Finland: { en: 'Finland', fi: 'Suomi', sv: 'Finland' },
  Suomi: { en: 'Finland', fi: 'Suomi', sv: 'Finland' },
  Europe: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
  Eurooppa: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
  Europa: { en: 'Europe', fi: 'Eurooppa', sv: 'Europa' },
};

/**
 * Builds the path segment for a location-filter SRP in a target locale (e.g. `Helsinki_Finland` → `Helsinki_Suomi` for fi).
 * Handles single-segment country/continent hubs (`Finland`, `Europe`) for hreflang and canonical URLs.
 */
export function generateLocalizedLocationUrl(locationValue: string, targetLocale: LocationLocale): string {
  if (!locationValue) return '';

  const parts = locationValue.split('_');

  if (parts.length === 1) {
    const row = SINGLE_SEGMENT_REGION_SLUGS[parts[0]];
    if (row) {
      return toLocationSlug(row[targetLocale]);
    }
    return locationValue;
  }

  if (parts.length < 2) return locationValue;

  const country = parts[parts.length - 1];
  const cityParts = parts.slice(0, -1);
  const cityName = cityParts.join(' ');

  const mappedCity = CITY_MAPPINGS[cityName]?.[targetLocale] ?? cityName;
  const mappedCountry = COUNTRY_MAPPINGS[country]?.[targetLocale] ?? country;

  return toLocationSlug(`${mappedCity}, ${mappedCountry}`);
}
